import React from "react";
import PropTypes, { element } from "prop-types";

import { format } from "d3-format";
import { timeFormat } from "d3-time-format";

import ErrorBoundary from "./ErrorBoundry";

import { ChartCanvas, Chart } from "react-stockcharts";
import {
  RSISeries,
  CandlestickSeries,
  LineSeries,
  MACDSeries,
} from "react-stockcharts/lib/series";

import { Label } from "react-stockcharts/lib/annotation";
import { XAxis, YAxis } from "react-stockcharts/lib/axes";
import {
  EdgeIndicator,
  MouseCoordinateX,
  MouseCoordinateY,
  CrossHairCursor,
} from "react-stockcharts/lib/coordinates";

import { discontinuousTimeScaleProvider } from "react-stockcharts/lib/scale";
import {
  HoverTooltip,
  RSITooltip,
  MACDTooltip,
} from "react-stockcharts/lib/tooltip";

import {
  StandardDeviationChannel,
  DrawingObjectSelector,
} from "react-stockcharts/lib/interactive";

import { ema, rsi, macd } from "react-stockcharts/lib/indicator";
import { fitWidth } from "react-stockcharts/lib/helper";
import { last } from "react-stockcharts/lib/utils";

import OHLCTooltip from "./OLHCTooltip";

const dateFormat = timeFormat("%Y-%m-%d");
const numberFormat = format(".2f");

const axisColor = "#ffffff"; // White color for the axis labels

const macdAppearance = {
  stroke: {
    macd: "#FF0000",
    signal: "#FFFFFF",
  },
  fill: {
    divergence: "#4682B4",
  },
};

const mouseEdgeAppearance = {
  textFill: "#542605",
  stroke: "#05233B",
  strokeOpacity: 1,
  strokeWidth: 3,
  arrowWidth: 5,
  fill: "#BCDEFA",
};

const sdChannelApperanceShortTerm = {
  stroke: "#0000FF",
  fillOpacity: 0,
  strokeOpacity: 1,
  strokeWidth: 1,
  edgeStrokeWidth: 2,
  edgeStroke: "#FFFFFF",
  r: 5,
};

const sdChannelApperanceLongTerm = {
  stroke: "#FFFFFF",
  fillOpacity: 0,
  strokeOpacity: 1,
  strokeDasharray: "SolidDash",
  strokeWidth: 1,
  edgeStrokeWidth: 2,
  edgeStroke: "#FFFFFF",
  r: 5,
};
const keyValues = ["high", "low"];

function generateIndicatorCharts(
  rsi,
  macd,
  adx,
  roc,
  bb,
  remainingHeight,
  indicatorHeight,
  xGrid,
  chartMap
) {
  var components = [];
  var multiplier = 0;
  if (rsi) {
    components.push(chartMap["rsi"](multiplier, indicatorHeight));
    multiplier++;
  }
  if (macd) {
    components.push(chartMap["macd"](multiplier, indicatorHeight));
    multiplier++;
  }
  if (adx) {
    components.push(chartMap["adx"](multiplier, indicatorHeight));
    multiplier++;
  }
  if (roc) {
    components.push(chartMap["roc"](multiplier, indicatorHeight));
    multiplier++;
  }
  if (bb) {
    components.push(chartMap["bb"](multiplier, indicatorHeight));
    multiplier++;
  }
  components.push(
    <Chart
      id={7}
      height={10}
      yExtents={[0, 100]}
      origin={(w, h) => [0, remainingHeight + multiplier * indicatorHeight]}
    >
      <XAxis
        axisAt="bottom"
        orient="bottom"
        tickStroke={axisColor}
        stroke={axisColor}
        {...xGrid}
      />
    </Chart>
  );
  return components;
}

class CandleStickChartWithHoverTooltip extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      xValue: null,
      currentItem: null,
    };
  }

  // Handler to synchronize tooltips across charts
  handleCrossHairChange = ({ xValue, currentItem }) => {
    console.log("hereeeeeeeeee");
    this.setState({ xValue, currentItem });
  };

  removeRandomValues(data) {
    if (data !== null && data !== undefined) {
      return data.map((item) => {
        const newItem = { ...item };
        const numberOfDeletion =
          Math.floor(Math.random() * keyValues.length) + 1;
        for (let i = 0; i < numberOfDeletion; i += 1) {
          const randomKey =
            keyValues[Math.floor(Math.random() * keyValues.length)];
          newItem[randomKey] = undefined;
        }
        return newItem;
      });
    }
  }

  sdChannelRanges(data) {
    let sdChannelRange = [];
    if (data.length >= 50) {
      const sdChannelRangeData = {
        start: [data.length - 50, data[data.length - 50].close],
        end: [data.length - 1, data[data.length - 1].close],
        appearance: sdChannelApperanceShortTerm,
      };
      sdChannelRange.push(sdChannelRangeData);
    }
    if (data.length >= 250) {
      const sdChannelRangeData = {
        start: [data.length - 250, data[data.length - 250].close],
        end: [data.length - 1, data[data.length - 1].close],
        appearance: sdChannelApperanceLongTerm,
      };
      sdChannelRange.push(sdChannelRangeData);
    }
    return sdChannelRange;
  }

  shouldComponentUpdate(nextProps, nextState) {
    // You can put your conditions for re-rendering here
    // For example, only re-render if the count is even
    if (
      nextProps.title !== this.props.title ||
      nextProps.rsi !== this.props.rsi ||
      nextProps.macd !== this.props.macd ||
      nextProps.adx !== this.props.adx ||
      nextProps.roc !== this.props.roc ||
      nextProps.bb !== this.props.bb ||
      nextProps.time !== this.props.time ||
      nextProps.chartMode !== this.props.chartMode ||
      nextProps?.data?.length !== this.props?.data?.length
    ) {
      console.log("Re-render");
      return true; // Component will re-render
    }
    return false; // Component will not re-render
  }

  render() {
    const {
      type,
      data: initialData,
      width,
      height,
      ratio,
      chartMode,
      adx,
      roc,
      bb,
    } = this.props;
    const rsiChart = this.props.rsi;
    const macdChard = this.props.macd;

    const ema20 = ema()
      .id(0)
      .options({ windowSize: 20 })
      .merge((d, c) => {
        d.ema20 = c;
      })
      .accessor((d) => d.ema20);

    const ema50 = ema()
      .id(1)
      .options({ windowSize: 50 })
      .merge((d, c) => {
        d.ema50 = c;
      })
      .accessor((d) => d.ema50);

    const rsiCalculator = rsi()
      .options({ windowSize: 14 })
      .merge((d, c) => {
        d.rsi = c;
      })
      .accessor((d) => d.rsi);

    const macdCalculator = macd()
      .options({ fast: 12, slow: 26, signal: 9 })
      .merge((d, c) => {
        d.macd = c;
      })
      .accessor((d) => d.macd);

    const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(
      (d) => d.date
    );
    const calculatedData = ema50(
      ema20(rsiCalculator(macdCalculator(initialData)))
    );
    const { data, xScale, xAccessor, displayXAccessor } =
      xScaleProvider(calculatedData);

    const margin = { left: 50, right: 50, top: 10, bottom: 30 };

    var gridHeight = height - margin.top - margin.bottom;
    var gridWidth = width - margin.left - margin.right;

    var yGrid = {
      innerTickSize: -1 * gridWidth,
      tickStrokeDasharray: "Solid",
      tickStrokeOpacity: 0.2,
      tickStrokeWidth: 1,
    };
    var xGrid = {
      innerTickSize: -1 * gridHeight,
      tickStrokeDasharray: "Solid",
      tickStrokeOpacity: 0.2,
      tickStrokeWidth: 1,
    };

    const numberOfIndicators = [rsiChart, macdChard, adx, roc, bb].filter(
      (v) => !!v
    ).length;
    console.log(numberOfIndicators);
    const remainingHeight = height * 0.5; // 50% height for all indicators combined
    const indicatorHeight = remainingHeight / 6; // Evenly distribute the remaining height

    const chartMap = {
      rsi: (multiplier, indicatorHeight) => (
        <Chart
          id={2}
          height={indicatorHeight}
          yExtents={[0, 100]}
          origin={(w, h) => [0, remainingHeight + multiplier * indicatorHeight]} // Start below the candlestick chart
          padding={{ top: 10, bottom: 10 }}
        >
          <YAxis
            axisAt="right"
            orient="right"
            ticks={2}
            tickStroke={axisColor}
            stroke={axisColor}
            {...yGrid}
          />
          <RSISeries
            yAccessor={(d) => d.rsi}
            stroke={{
              line: "#000000",
              top: "#FF0000",
              middle: "#000000",
              bottom: "#00FF00",
              outsideThreshold: "#b300b3",
              insideThreshold: "#FFFFFF",
            }}
            strokeDasharray={{
              line: "Solid",
              top: "Solid",
              middle: "ShortDash",
              bottom: "Solid",
            }}
          />
        </Chart>
      ),
      macd: (multiplier, indicatorHeight) => (
        <Chart
          id={3}
          height={indicatorHeight}
          yExtents={macdCalculator.accessor()}
          origin={(w, h) => [0, remainingHeight + multiplier * indicatorHeight]} // Start below the previous chart
          padding={{ top: 10, bottom: 10 }}
        >
          <YAxis
            axisAt="right"
            orient="right"
            ticks={2}
            tickStroke={axisColor}
            stroke={axisColor}
            {...yGrid}
          />
          <MACDSeries
            yAccessor={macdCalculator.accessor()}
            {...macdAppearance}
          />
        </Chart>
      ),
      adx: (multiplier, indicatorHeight) => (
        <Chart
          id={4}
          height={indicatorHeight}
          yExtents={(d) => [d.adx, d.dmi_plus, d.dmi_minus]}
          origin={(w, h) => [0, remainingHeight + multiplier * indicatorHeight]} // Start below the previous chart
          padding={{ top: 10, bottom: 10 }}
        >
          <YAxis
            axisAt="right"
            orient="right"
            ticks={2}
            tickStroke={axisColor}
            stroke={axisColor}
            {...yGrid}
          />
          <LineSeries yAccessor={(d) => d.adx} stroke="#FFFFFF" />
          <LineSeries yAccessor={(d) => d.dmi_plus} stroke="#00FF00" />
          <LineSeries yAccessor={(d) => d.dmi_minus} stroke="#FF0000" />
        </Chart>
      ),
      roc: (multiplier, indicatorHeight) => (
        <Chart
          id={5}
          height={indicatorHeight}
          yExtents={(d) => [d.roc5, d.roc20]}
          origin={(w, h) => [0, remainingHeight + multiplier * indicatorHeight]} // Start below the previous chart
          padding={{ top: 10, bottom: 10 }}
        >
          <YAxis
            axisAt="right"
            orient="right"
            ticks={2}
            tickStroke={axisColor}
            stroke={axisColor}
            {...yGrid}
          />
          <LineSeries yAccessor={(d) => d.roc5} stroke="#FFFF00" />
          <LineSeries yAccessor={(d) => d.roc20} stroke="#FFFF00" />
        </Chart>
      ),
      bb: (multiplier, indicatorHeight) => (
        <Chart
          id={6}
          height={indicatorHeight}
          yExtents={(d) => d.bb_width}
          origin={(w, h) => [0, remainingHeight + multiplier * indicatorHeight]} // Start below the previous chart
          padding={{ top: 10, bottom: 10 }}
        >
          <YAxis
            axisAt="right"
            orient="right"
            ticks={2}
            tickStroke={axisColor}
            stroke={axisColor}
            {...yGrid}
          />
          <LineSeries yAccessor={(d) => d.bb_width} stroke="#FFFFFF" />
        </Chart>
      ),
    };

    return (
      <ErrorBoundary>
        <ChartCanvas
          height={remainingHeight + indicatorHeight * (numberOfIndicators + 1)}
          width={width}
          ratio={ratio}
          type={type}
          seriesName="Series"
          data={data}
          padding={{ left: 30, right: 30 }}
          margin={margin}
          xScale={xScale}
          clamp={"both"}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          xExtents={[xAccessor(data[0]), xAccessor(data[data.length - 1])]}
        >
          {/* Candlestick Chart (50% of height) */}
          <Chart
            id={1}
            height={remainingHeight}
            yExtents={(d) => [d.high, d.low, d.ema20, d.ema50]}
            padding={{ top: 10, bottom: 20 }}
          >
            <YAxis
              axisAt="right"
              orient="right"
              tickStroke={axisColor}
              stroke={axisColor}
              {...yGrid}
            />
            {chartMode === 0 ? (
              <CandlestickSeries
                wickStroke={(d) => (d.close > d.open ? "#FFFFF" : "#87CEFA")}
                fill={(d) => (d.close > d.open ? "#FFFFFE" : "#87CEFA")}
              />
            ) : (
              <LineSeries yAccessor={(d) => d.close} stroke="#ff0000" />
            )}
            <LineSeries yAccessor={ema20.accessor()} stroke={ema20.stroke()} />
            <LineSeries yAccessor={ema50.accessor()} stroke={ema50.stroke()} />

            <StandardDeviationChannel
                    enabled={false}
                    channels={this.sdChannelRanges(data)}
                  />

            <OHLCTooltip origin={[-40, 0]} textFill="#FFFFFF" />
          </Chart>
          {generateIndicatorCharts(
            rsiChart,
            macdChard,
            adx,
            roc,
            bb,
            remainingHeight,
            indicatorHeight,
            xGrid,
            chartMap
          ).map((element, index) => {
            return element;
          })}
        </ChartCanvas>
      </ErrorBoundary>
    );
  }
}

CandleStickChartWithHoverTooltip.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  ratio: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["svg", "hybrid"]).isRequired,
};

CandleStickChartWithHoverTooltip.defaultProps = {
  type: "hybrid",
};
CandleStickChartWithHoverTooltip = fitWidth(CandleStickChartWithHoverTooltip);

export default CandleStickChartWithHoverTooltip;
